<template>
    <v-dialog v-model="internalValue" max-width="95%" persistent scrollable>
        <v-card>
            <v-card-title>
                Importar funcionarios desde Excel
                <v-spacer></v-spacer>
                <v-btn icon @click="cerrar">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-alert dense outlined type="info" class="mb-3">
                    Máximo permitido por archivo: {{ maxRegistrosExcel }} registros.
                </v-alert>

                <v-file-input
                    v-model="archivo"
                    label="Seleccione archivo Excel"
                    accept=".xlsx,.xls"
                    outlined
                    dense
                    prepend-icon="mdi-microsoft-excel"
                    :disabled="loading || importando"
                    @change="limpiarResultado"
                />

                <v-btn
                    color="#5e672c"
                    dark
                    class="mt-2"
                    :loading="loading"
                    :disabled="!archivo || importando"
                    @click="validarExcel"
                >
                    Validar archivo
                </v-btn>

                <v-row v-if="resumen" class="mt-4">
                    <v-col cols="12" md="3">
                        <v-alert type="info" dense>Total: {{ resumen.total }}</v-alert>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-alert type="success" dense>Válidos: {{ resumen.validos }}</v-alert>
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-alert type="error" dense>Errores: {{ resumen.errores }}</v-alert>
                    </v-col>
                </v-row>

                <v-alert
                    v-if="resumen && puedeImportar"
                    type="success"
                    dense
                    outlined
                    class="mt-3"
                >
                    El archivo fue validado correctamente. Puede importar los funcionarios.
                </v-alert>

                <v-alert
                    v-if="inconsistencias.length"
                    type="warning"
                    dense
                    outlined
                    class="mt-3"
                >
                    Se encontraron inconsistencias. Corrija el Excel y vuelva a validar.
                </v-alert>

                <v-data-table
                    v-if="inconsistencias.length"
                    :headers="headersErrores"
                    :items="inconsistencias"
                    dense
                    class="mt-4"
                    :items-per-page="10"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                />

                <v-data-table
                    v-if="filasValidas.length"
                    :headers="headersPreview"
                    :items="filasValidas"
                    dense
                    class="mt-4"
                    :items-per-page="10"
                    :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                />
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="cerrar" :disabled="loading || importando">
                    Cancelar
                </v-btn>
                <v-btn
                    color="#5e672c"
                    dark
                    :loading="importando"
                    :disabled="!puedeImportar || loading"
                    @click="importarExcel"
                >
                    Importar funcionarios
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';

export default {
    name: 'ImportarFuncionarios',

    props: {
        value: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            maxRegistrosExcel: 500,

            archivo: null,
            loading: false,
            importando: false,
            resumen: null,
            filasValidas: [],
            inconsistencias: [],

            headersErrores: [
                { text: 'Fila', value: 'fila' },
                { text: 'Campo', value: 'campo' },
                { text: 'Valor', value: 'valor' },
                { text: 'Error', value: 'mensaje' }
            ],

            headersPreview: [
                { text: 'Documento', value: 'documento' },
                { text: 'Nombres', value: 'nombres' },
                { text: 'Primer apellido', value: 'primero_apellido' },
                { text: 'Correo', value: 'correo' },
                { text: 'Nro. escalafón', value: 'cl1' },
                { text: 'Documento supervisor', value: 'cl2' }
            ]
        };
    },

    computed: {
        internalValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },

        puedeImportar() {
            return this.filasValidas.length > 0 && this.inconsistencias.length === 0;
        }
    },

    methods: {
        cerrar() {
            if (this.loading || this.importando) return;

            this.archivo = null;
            this.limpiarResultado();
            this.internalValue = false;
        },

        limpiarResultado() {
            this.resumen = null;
            this.filasValidas = [];
            this.inconsistencias = [];
        },

        async contarFilasExcel(file) {
            const data = await file.arrayBuffer();

            const workbook = XLSX.read(data, {
                type: 'array'
            });

            const sheet = workbook.Sheets['Funcionarios'];

            if (!sheet) {
                throw new Error('El archivo no contiene la hoja Funcionarios');
            }

            const rows = XLSX.utils.sheet_to_json(sheet, {
                header: 1,
                defval: ''
            });

            const dataRows = rows.slice(1).filter(row =>
                row.some(cell => String(cell || '').trim() !== '')
            );

            return dataRows.length;
        },

        async validarExcel() {
            if (!this.archivo) return;

            this.loading = true;
            this.limpiarResultado();

            try {
                const totalFilas = await this.contarFilasExcel(this.archivo);

                if (totalFilas === 0) {
                    this.$emit('error', 'El archivo no contiene registros para validar.');
                    return;
                }

                if (totalFilas > this.maxRegistrosExcel) {
                    this.$emit(
                        'error',
                        `El archivo tiene ${totalFilas} registros. El máximo permitido es ${this.maxRegistrosExcel}.`
                    );
                    return;
                }

                const formData = new FormData();
                formData.append('archivo', this.archivo);

                const response = await axios.post(
                    'api/PersonaInfoGenerals/ValidarExcelFuncionarios',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                this.resumen = {
                    total: response.data.total || 0,
                    validos: response.data.validos || 0,
                    errores: response.data.errores || 0
                };

                this.filasValidas = response.data.filasValidas || [];
                this.inconsistencias = response.data.inconsistencias || [];

            } catch (error) {
                console.error(error);

                const mensaje =
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.message ||
                    'Error al validar el archivo';

                this.$emit('error', mensaje);
            } finally {
                this.loading = false;
            }
        },

        async importarExcelOld() {
            if (!this.puedeImportar) return;

            this.importando = true;

            try {
                await axios.post('api/PersonaInfoGenerals/ImportarExcelFuncionarios', {
                    filas: this.filasValidas,
                    oid_user: Number(localStorage.getItem('oidu'))
                });

                this.$emit('importado');
                this.$emit('success', 'Funcionarios importados correctamente');
                this.cerrar();

            } catch (error) {
                console.error(error);

                const mensaje =
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    'Error al importar funcionarios';

                this.$emit('error', mensaje);
            } finally {
                this.importando = false;
            }
        },
        
        async importarExcel() {
            if (!this.puedeImportar) return;

            this.importando = true;

            try {
                const response = await axios.post(
                    'api/PersonaInfoGenerals/ImportarExcelFuncionarios',
                    {
                        filas: this.filasValidas,
                        oid_user: Number(localStorage.getItem('oidu'))
                    }
                );

                // Limpiar inmediatamente
                this.archivo = null;
                this.resumen = null;
                this.filasValidas = [];
                this.inconsistencias = [];

                // Cerrar dialog
                this.internalValue = false;

                // Recargar listado
                this.$emit('importado');

                // Mostrar mensaje
                this.$emit(
                    'success',
                    response.data?.message ||
                    'Funcionarios importados correctamente'
                );

            } catch (error) {
                console.error(error);

                const mensaje =
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    'Error al importar funcionarios';

                this.$emit('error', mensaje);

            } finally {
                this.importando = false;
            }
        }
    }
};
</script>